/**
 * composioAuth.js — Shared Composio OAuth utility for inline app connections.
 *
 * Usage:
 *   import { connectApp, checkConnectionStatus, APP_SLUG_MAP } from '../utils/composioAuth';
 *   const result = await connectApp('Gmail', orgId);
 *   if (result.redirect_url) window.open(result.redirect_url, ...);
 */
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from './api';

/* ─── App Name → Composio Slug Mapping ────────────────── */
const APP_SLUG_MAP = {
  'Gmail':                    'gmail',
  'Outlook':                  'outlook',
  'Google Calendar':          'googlecalendar',
  'Google Sheets':            'googlesheets',
  'Google Drive':             'googledrive',
  'Google Business Profile':  'googlebusinessprofile',
  'Slack':                    'slack',
  'HubSpot':                  'hubspot',
  'Salesforce':               'salesforce',
  'Pipedrive':                'pipedrive',
  'Calendly':                 'calendly',
  'Zoom':                     'zoom',
  'WhatsApp':                 'whatsapp',
  'Xero':                     'xero',
  'QuickBooks':               'quickbooks_sandbox',
  'MYOB':                     'myob',
  'Stripe':                   'stripe',
  'Mailchimp':                'mailchimp',
  'Zapier':                   'zapier',
  'Facebook':                 'facebook',
  'Instagram':                'instagram',
  'Trustpilot':               'trustpilot',
  'Airtable':                 'airtable',
  'Airtable CRM':             'airtable',
  'Discord':                  'discord',
  'Microsoft Teams':          'microsoft_teams',
  'SendGrid':                 'sendgrid',
  'Twilio':                   'twilio',
  'Notion':                   'notion',
  'Asana':                    'asana',
  'Trello':                   'trello',
  'ClickUp':                  'clickup',
  'Monday':                   'monday',
  'Monday.com':               'monday',
  'Linear':                   'linear',
  'Jira':                     'jira',
  'GitHub':                   'github',
  'GitLab':                   'gitlab',
  'Intercom':                 'intercom',
  'Zendesk':                  'zendesk',
  'Freshdesk':                'freshdesk',
  'Shopify':                  'shopify',
  'WooCommerce':              'woocommerce',
  'BigCommerce':              'bigcommerce',
  'Square':                   'square',
  'PayPal':                   'paypal',
  'Dropbox':                  'dropbox',
  'OneDrive':                 'onedrive',
  'Box':                      'box',
  'Typeform':                 'typeform',
  'Jotform':                  'jotform',
  'SurveyMonkey':             'surveymonkey',
  'ActiveCampaign':           'activecampaign',
  'ConvertKit':               'convertkit',
  'Klaviyo':                  'klaviyo',
};

/* ─── Auth Provider Groups (shared OAuth consent) ─────── */
const AUTH_PROVIDERS = {
  'Gmail':                    'google',
  'Google Calendar':          'google',
  'Google Sheets':            'google',
  'Google Drive':             'google',
  'Google Business Profile':  'google',
  'Outlook':                  'microsoft',
};

/**
 * Get the Composio slug for a display app name.
 */
function getSlug(appName) {
  return APP_SLUG_MAP[appName] || appName.toLowerCase().replace(/\s+/g, '');
}

/**
 * Get the OAuth provider group for an app (for dedup).
 */
function getAuthProvider(appName) {
  return AUTH_PROVIDERS[appName] || appName;
}

/**
 * Get a user-facing label for the Connect button.
 */
function getConnectLabel(appName) {
  const provider = AUTH_PROVIDERS[appName];
  if (provider === 'google') return 'Connect Google';
  if (provider === 'microsoft') return 'Connect Microsoft';
  return `Connect ${appName}`;
}

/**
 * Initiate OAuth connection for an app.
 * Returns { redirect_url, connected_account_id } on success.
 */
async function connectApp(appName, orgId, callbackUrl) {
  const slug = getSlug(appName);
  const callback = callbackUrl || `${window.location.origin}/oauth/callback?connected=${slug}`;

  const res = await fetchWithOrg(
    `${BACKEND_URL}/api/service-os/integrations/connect/${slug}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ callback_url: callback }),
    },
    orgId
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to connect ${appName}`);
  }

  return res.json();
}

/**
 * Check connection status for all connected apps.
 * Returns { connected_count, connected_apps: string[] }
 */
async function checkConnectionStatus(orgId) {
  const res = await fetchWithOrg(
    `${BACKEND_URL}/api/service-os/integrations/status`,
    {},
    orgId
  );
  if (!res.ok) return { connected_count: 0, connected_apps: [] };
  return res.json();
}

/**
 * Open the OAuth popup and return a promise that resolves when
 * the callback page posts back a message OR the popup closes.
 * Uses postMessage to avoid Cross-Origin-Opener-Policy issues.
 */
function openOAuthPopup(redirectUrl) {
  const width = 600;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const popup = window.open(
    redirectUrl,
    'composio_oauth',
    `width=${width},height=${height},left=${left},top=${top},popup=yes`
  );

  return new Promise((resolve) => {
    if (!popup) {
      resolve({ closed: true, slug: null });
      return;
    }

    let resolved = false;

    // Listen for postMessage from our /oauth/callback page
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'composio_oauth_complete') {
        cleanup();
        resolve({ closed: true, slug: event.data.slug });
      }
    };

    // Fallback: poll for popup closed (in case redirect doesn't reach our callback)
    const fallbackTimer = setInterval(() => {
      try {
        if (popup.closed) {
          cleanup();
          resolve({ closed: true, slug: null });
        }
      } catch {
        // COOP may block this — just ignore and rely on postMessage
      }
    }, 1000);

    // Safety timeout — resolve after 3 minutes no matter what
    const safetyTimeout = setTimeout(() => {
      cleanup();
      try { popup.close(); } catch {}
      resolve({ closed: true, slug: null });
    }, 180000);

    function cleanup() {
      if (resolved) return;
      resolved = true;
      window.removeEventListener('message', onMessage);
      clearInterval(fallbackTimer);
      clearTimeout(safetyTimeout);
    }

    window.addEventListener('message', onMessage);
  });
}

export {
  APP_SLUG_MAP,
  AUTH_PROVIDERS,
  getSlug,
  getAuthProvider,
  getConnectLabel,
  connectApp,
  checkConnectionStatus,
  openOAuthPopup,
};
